import { useCallback, useEffect, useState } from "react";
import type { TaskItem } from "@edge/shared";
import {
  createPersonalTask,
  deletePersonalTask,
  fetchPersonalTasks,
  updatePersonalTask,
} from "../api";

export interface PersonalTaskInput {
  subject: string;
  description?: string | null;
  due_date?: string | null;
  priority?: "high" | "normal" | "low";
  /** Target Todoist project id (create only). Omit ⇒ Todoist Inbox. */
  project_id?: string | null;
}

export interface PersonalTasksState {
  tasks: TaskItem[];
  loading: boolean;
  error: string | null;
  /** Increments on every mutation. Consumers can use it as a refetch signal. */
  version: number;
  create: (input: PersonalTaskInput) => Promise<void>;
  update: (id: string, patch: PersonalTaskInput) => Promise<void>;
  /** Set a new due date without opening the full edit form. Clears any snooze. */
  reschedule: (id: string, dueDate: string | null) => Promise<void>;
  complete: (task: TaskItem) => Promise<void>;
  snooze: (task: TaskItem) => Promise<void>;
  remove: (task: TaskItem) => Promise<void>;
  reload: () => void;
}

export function usePersonalTasks(): PersonalTasksState {
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPersonalTasks()
      .then((rows) => {
        if (!cancelled) {
          setTasks(rows);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "fetch_failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const create = useCallback(async (input: PersonalTaskInput) => {
    const subject = input.subject.trim();
    if (!subject) return;
    await createPersonalTask({
      subject,
      description: input.description?.trim() || undefined,
      due_date: input.due_date ?? undefined,
      priority:
        input.priority && input.priority !== "normal" ? input.priority : undefined,
      project_id: input.project_id ?? undefined,
    });
    setReloadKey((k) => k + 1);
  }, []);

  const update = useCallback(async (id: string, patch: PersonalTaskInput) => {
    await updatePersonalTask(id, {
      subject: patch.subject?.trim() || undefined,
      description: patch.description === null ? null : patch.description,
      due_date: patch.due_date === null ? null : patch.due_date,
      priority: patch.priority,
    });
    setReloadKey((k) => k + 1);
  }, []);

  const reschedule = useCallback(async (id: string, dueDate: string | null) => {
    try {
      await updatePersonalTask(id, { due_date: dueDate });
    } catch (err) {
      // 409 recurring_readonly: a recurring task's date is read-only in Edge.
      // Surface briefly and revert by reloading the source of truth.
      setError(err instanceof Error ? err.message : "reschedule_failed");
    } finally {
      setReloadKey((k) => k + 1);
    }
  }, []);

  const complete = useCallback(async (task: TaskItem) => {
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    try {
      await updatePersonalTask(task.id, { completed: true });
    } catch {
      setReloadKey((k) => k + 1);
    }
  }, []);

  const snooze = useCallback(async (task: TaskItem) => {
    try {
      await updatePersonalTask(task.id, { snooze_days: 1 });
    } finally {
      setReloadKey((k) => k + 1);
    }
  }, []);

  const remove = useCallback(async (task: TaskItem) => {
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    try {
      await deletePersonalTask(task.id);
    } catch {
      setReloadKey((k) => k + 1);
    }
  }, []);

  return {
    tasks,
    loading,
    error,
    version: reloadKey,
    create,
    update,
    reschedule,
    complete,
    snooze,
    remove,
    reload: () => setReloadKey((k) => k + 1),
  };
}
